import TopNavBar from "./TopNavBar";
import Modal from "./Modal";
import { useEffect, useState } from "react";
import { projectURL } from "./utils/mockdata";

const DEFAULT_LIMIT = 5;

const normalizeResponseItems = (json) => {
    // try several common shapes returned by APIs
    if (!json) return [];
    if (Array.isArray(json)) return json;
    if (Array.isArray(json.helpCenter)) return json.helpCenter;
    if (Array.isArray(json.data)) return json.data;
    if (Array.isArray(json.items)) return json.items;
    if (Array.isArray(json.results)) return json.results;
    return [];
};

const ViewHelpCenter = () => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [totalPages, setTotalPages] = useState(1);
    const [updatingId, setUpdatingId] = useState(null);
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [modalType, setModalType] = useState("info");

    const showModal = (title, message, type = "info") => {
        setModalTitle(title);
        setModalMessage(message);
        setModalType(type);
        setModalOpen(true);
    };

    setTimeout(() => {
        setModalOpen(false);
    }, 4000);

    useEffect(() => {
        fetchPage(page, limit);
    }, [page, limit]);

    const fetchPage = async (p = 1, l = DEFAULT_LIMIT) => {
        setLoading(true);
        setError("");
        try {
            const url = `${projectURL}profiles/view-help-center?page=${p}&limit=${l}`;
            const res = await fetch(url, { mode: "cors" });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `${res.status} ${res.statusText}`);
            }
            const json = await res.json();

            const fetched = normalizeResponseItems(json);
            setItems(fetched);

            // try common pagination metadata
            const maybeTotal = json.total || json.count || json.totalCount || json.total_items || json.totalItems;
            const maybeLimit = l;
            if (maybeTotal && maybeLimit) {
                setTotalPages(Math.max(1, Math.ceil(maybeTotal / maybeLimit)));
            } else if (json.totalPages) {
                setTotalPages(json.totalPages);
            } else if (json.pages) {
                setTotalPages(json.pages);
            } else {
                // fallback: if we got less than limit assume last page
                setTotalPages(fetched.length < l ? p : Math.max(p, 2));
            }
        } catch (err) {
            console.error("Error fetching help center:", err);
            setError("Failed to load help center entries.");
            setItems([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const gotoPage = (p) => {
        if (p < 1) return;
        if (p > totalPages) return;
        setPage(p);
    };

    const toggleMenu = (itemId, e) => {
        e.stopPropagation();
        setMenuOpenId(menuOpenId === itemId ? null : itemId);
    };

    const handleStatusUpdate = async (item, newStatus) => {
        if (!item._id && !item.id) {
            console.error("No ID found for item:", item);
            return;
        }

        const itemId = item._id || item.id;
        setUpdatingId(itemId);
        setMenuOpenId(null); // Close the menu

        try {
            const response = await fetch(projectURL + "profiles/update-Help-Center", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    id: itemId,
                    status: newStatus // Send the new status to the API
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log("Update successful:", result);

            // Update the local state to reflect the status change
            setItems(prevItems =>
                prevItems.map(it => {
                    if ((it._id === itemId) || (it.id === itemId)) {
                        return { ...it, status: newStatus };
                    }
                    return it;
                })
            );

            // Show success message using modal
            showModal("Success", `Status updated to "${newStatus}" successfully!`, "success");

        } catch (error) {
            console.error("Error updating help center status:", error);
            showModal("Error", "Failed to update status. Please try again.", "error");
        } finally {
            setUpdatingId(null);
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setMenuOpenId(null);
        };

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    const renderItem = (it, idx) => {
        const isComplete = it.status === "complete";
        const titleColor = isComplete ? "text-green-800" : "text-red-800";
        const textColor = isComplete ? "text-green-500" : "text-red-500";
        const detailColor = isComplete ? "text-green-400" : "text-red-400";
        const borderColor = isComplete ? "border-green-200" : "border-red-200";
        const hoverBg = "hover:bg-gray-50";

        const title = it.title || it.subject || it.heading || 'Untitled';
        const summary = it.summary || it.description ||
            (it.body && typeof it.body === 'string' ?
                it.body.slice(0, 160) + (it.body.length > 160 ? '...' : '') : '');
        const author = it.username ? it.username : 'Unknown';
        const email = it.email ? `(${it.email})` : "(-)";
        const date = it.created_at ? new Date(it.created_at).toLocaleDateString() : it.date || '';
        const isUpdating = updatingId === (it._id || it.id);
        const itemId = it._id || it.id || idx;
        const isMenuOpen = menuOpenId === itemId;

        return (
            <li
                key={itemId}
                className={`border rounded-md p-4 hover:shadow-md transition-shadow ${borderColor} ${hoverBg} ${isUpdating ? 'opacity-50' : ''}`}
            >
                {isUpdating && (
                    <div className="flex justify-center mb-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                    </div>
                )}

                <div className="flex items-start justify-between">
                    <div className="flex-1 mr-4">
                        <h3 className={`text-lg font-medium ${titleColor}`}>{title}</h3>
                        <p className={`text-md ${textColor} mt-1`}>{summary}</p>
                        <i className={`text-sm ${textColor} mt-1`}>-By {author} {email}</i>
                    </div>
                    <div className="flex flex-col items-end">
                        <div className="flex items-center space-x-2">
                            <div className={`text-xs ${detailColor}`}>{date}</div>
                            <div className="relative">
                                <button
                                    onClick={(e) => toggleMenu(itemId, e)}
                                    className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    aria-label="Toggle menu"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 text-gray-500"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                    </svg>
                                </button>

                                {/* Dropdown Menu */}
                                {isMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                        <div className="py-1">
                                            <button
                                                onClick={() => handleStatusUpdate(it, "complete")}
                                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-green-50"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Resolve
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={`mt-2 px-2 py-1 text-xs rounded-full ${isComplete ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {isComplete ? "Complete" : "Incomplete"}
                        </div>
                    </div>
                </div>

            </li>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
            <TopNavBar />
            <div className="container mx-auto px-4 py-8 max-w-4xl">
                <header className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">Help Center Articles</h1>
                </header>

                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="p-4 md:p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <label className="text-sm text-gray-600">Show</label>
                                <select
                                    value={limit}
                                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                                    className="rounded-md border-gray-200 p-1"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                </select>
                                <span className="text-sm text-gray-500">per page</span>
                            </div>

                            <div className="text-sm text-gray-500">Page {page} of {totalPages}</div>
                        </div>

                        {loading ? (
                            <div className="space-y-3">
                                {[...Array(limit)].map((_, i) => (
                                    <div key={i} className="animate-pulse bg-gray-100 rounded-md h-16" />
                                ))}
                            </div>
                        ) : error ? (
                            <div className="text-red-600 text-sm">{error}</div>
                        ) : items.length === 0 ? (
                            <div className="text-gray-600">No help articles found.</div>
                        ) : (
                            <ul className="space-y-3">
                                {items.map(renderItem)}
                            </ul>
                        )}

                        {/* Pagination controls */}
                        <div className="mt-6 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => gotoPage(page - 1)}
                                    disabled={page <= 1}
                                    className={`px-3 py-1 rounded-md border ${page <= 1 ? 'text-gray-300 border-gray-200' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    Previous
                                </button>

                                {/* show a small range of pages */}
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                                        const start = Math.max(1, page - 2);
                                        const p = start + i;
                                        if (p > totalPages) return null;
                                        return (
                                            <button
                                                key={p}
                                                onClick={() => gotoPage(p)}
                                                className={`px-3 py-1 rounded-md border ${p === page ? 'bg-indigo-600 text-white border-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                {p}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex items-center">
                                <button
                                    onClick={() => gotoPage(page + 1)}
                                    disabled={page >= totalPages}
                                    className={`px-3 py-1 rounded-md border ${page >= totalPages ? 'text-gray-300 border-gray-200' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title={modalTitle}
                message={modalMessage}
                type={modalType}
            />
        </div>
    );
};

export default ViewHelpCenter;
